import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from 'axios';

import { useOrganizationStore } from './organization';
import { useAuthStore } from './auth';
import { resolveIncidentRequestOrganization } from './helpers';
import { getEnv } from '@/config/projectEnvVariables';
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipToast?: boolean;
        throwOnError?: boolean;
    }
}

type InvalidTokenResponse = {
    success?: boolean;
    message?: string;
    error?: string;
};

export function isInvalidTokenResponse(error: unknown): boolean {
    if (!error || typeof error !== 'object' || !('response' in error)) {
        return false;
    }

    const response = (error as { response?: { status?: number; data?: unknown } }).response;
    const data = response?.data as InvalidTokenResponse | undefined;

    return (
        response?.status === 401 &&
        data?.success === false &&
        data?.message === 'Invalid token supplied' &&
        data?.error === 'unauthorized'
    );
}

function handleInvalidTokenResponse(error: unknown): boolean {
    if (!isInvalidTokenResponse(error)) {
        return false;
    }

    void useAuthStore().expireSession();
    return true;
}

// Support
export const axiosSupportInstance = axios.create({});

axiosSupportInstance.interceptors.request.use(async (config) => {
    config.baseURL = getEnv().VITE_ORGANIZATION_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

    return config;
});

axiosSupportInstance.interceptors.response.use(undefined, (error) => {
    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if (error.response && (error.response.status === 401 || error.response.status === 400)) {
        useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
    } else if (error.response && error.response.status === 403) {
        useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
    }

    return Promise.reject(error);
});

// SLA access — deliberately NO X-Organization-Id and no error toasts: sla/permissions answers
// from the token alone, must work before any organization is selected, and a failure simply
// leaves the SLA surface hidden (the store fails closed).
export const axiosSlaInstance = axios.create({});

axiosSlaInstance.interceptors.request.use(async (config) => {
    config.baseURL = getEnv().VITE_INTEGRATION_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    return config;
});

axiosSlaInstance.interceptors.response.use(undefined, (error) => {
    handleInvalidTokenResponse(error);
    return Promise.reject(error);
});

// Incident
export const axiosIncidentInstance = axios.create({});

axiosIncidentInstance.interceptors.request.use(async (config) => {
    const organizationStore = useOrganizationStore();
    // In admin/global view, incident operations always act as the atNorth home org so the
    // NOC operator gets the full internal catalog/create flow, independent of any customer
    // org left selected in org view. See resolveIncidentRequestOrganization.
    const isAdminView = JSON.parse(localStorage.getItem('admin') ?? 'false') === true;
    const requestOrganization = resolveIncidentRequestOrganization(
        isAdminView,
        organizationStore.availableOrganizations,
        organizationStore.usersOrganization,
    );

    config.baseURL = getEnv().VITE_INTEGRATION_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    config.headers['X-Organization-Id'] = `${requestOrganization?.organization_uuid}`;

    return config;
});

axiosIncidentInstance.interceptors.response.use(undefined, (error) => {
    const skip = error?.config?.skipToast === true;

    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if (!skip) {
        if (error.response && (error.response.status === 401 || error.response.status === 400)) {
            useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
        } else if (error.response && error.response.status === 403) {
            useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
        }
    }

    return Promise.reject(error);
});

export const axiosV2APIInstance = axios.create({});

axiosV2APIInstance.interceptors.request.use(async (config) => {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    config.headers['X-Organization-Id'] = `${useOrganizationStore().usersOrganization?.organization_uuid}`;
    config.headers.AccessControlAllowOrigin = '*';

    return config;
});

axiosV2APIInstance.interceptors.response.use(undefined, (error) => {
    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
        useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
    } else if (error.response && error.response.status === 403) {
        useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
    }

    return Promise.reject(error);
});
/**
 * Envelope produced by legacy-org-api's return-parser middleware (and mirrored by the other Node lanes).
 * `data` is whatever the route returned; `metadata` only appears on paginated endpoints.
 */
export type ApiEnvelope<T = unknown> = {
    success: boolean;
    message?: string;
    code?: number;
    data: T;
    metadata?: unknown;
};

export async function makeRequest<T = unknown>(
    method: Method,
    url: string,
    axiosInstance: AxiosInstance,
    body?: unknown,
    config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
    const { throwOnError, ...requestConfig } = config;
    const tokenExpires = localStorage.getItem('tokenExpires');
    const token = localStorage.getItem('token');
    if (!(token === null || tokenExpires === null || Number(tokenExpires) * 1000 < Date.now())) {
        try {
            // Adjust the method call based on the passed method type
            const response = await axiosInstance.request<T>({
                url,
                method,
                data: body, // Add the body to the request
                responseType: requestConfig.responseType || 'json', // Default to 'json' if not specified
                ...requestConfig,
            });
            return response;
        } catch (error) {
            const isCanceled =
                (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') ||
                (error &&
                    typeof error === 'object' &&
                    'name' in error &&
                    (error.name === 'AbortError' || error.name === 'CanceledError'));
            if (isCanceled) {
                if (throwOnError) {
                    throw error;
                }
                return {} as AxiosResponse<T>;
            }
            if (axios.isAxiosError(error)) {
                console.error('Axios request failed:', error.message);
            }
            if (throwOnError) {
                throw error;
            }
            return {} as AxiosResponse<T>;
            // throw error; // TODO add later
        }
    } else {
        useAuthStore().refreshToken();
    }
    return {} as AxiosResponse<T>;
}
export const axiosOrgInstance = axios.create({});

axiosOrgInstance.interceptors.request.use(async (config) => {
    config.baseURL = getEnv().VITE_ORGANIZATION_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

    return config;
});

axiosOrgInstance.interceptors.response.use(undefined, (error) => {
    const skip = error?.config?.skipToast === true;

    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if (!skip) {
        if (error.response && error.response.status === 401) {
            useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
        } else if (error.response.status === 400) {
            useOrganizationStore().setToastMessage(error.response?.data?.message ?? 'Invalid request', 'warning');
        } else if (error.response && error.response.status === 403) {
            useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
        } else if (!error.response?.status) {
            useOrganizationStore().setToastMessage('Network error. Please check your connection.', 'warning');
        } else {
            useOrganizationStore().setToastMessage('Something went wrong', 'warning'); // consider removing this, we can have a more meaningful error message higher up the call-stack
        }
    }

    return Promise.reject(error);
});

// Infrastrucure API
export const axiosInfraInstance = axios.create({});

axiosInfraInstance.interceptors.request.use(async (config) => {
    config.baseURL = getEnv().VITE_INFRASTRUCTURE_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

    return config;
});

axiosInfraInstance.interceptors.response.use(undefined, (error) => {
    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if ((error.response && error.response.status === 401) || error.response.status === 400) {
        useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
    } else if (error.response && error.response.status === 403) {
        useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
    }

    return Promise.reject(error);
});

// .NET organization API
export const axiosDotNetOrgInstance = axios.create({});

axiosDotNetOrgInstance.interceptors.request.use(async (config) => {
    config.baseURL = getEnv().VITE_DOTNET_ORGANIZATION_API_HOST;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    config.headers['X-Organization-Id'] = `${useOrganizationStore().usersOrganization?.organization_uuid}`;

    config.headers.AccessControlAllowOrigin = '*';
    return config;
});

axiosDotNetOrgInstance.interceptors.response.use(undefined, (error) => {
    const skip = error?.config?.skipToast === true;

    if (handleInvalidTokenResponse(error)) {
        return Promise.reject(error);
    }

    if (!skip) {
        if (error.response && error.response.status === 401) {
            useOrganizationStore().setToastMessage('You dont have access to this request', 'warning');
        } else if (error.response && error.response.status === 403) {
            useOrganizationStore().setToastMessage('You dont have access to this route', 'warning');
        } else if (!error.response?.status) {
            useOrganizationStore().setToastMessage('Network error. Please check your connection.', 'warning');
        }
    }

    return Promise.reject(error);
});
